import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { streamAgentMessage } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Loader2, Plus, Mic, MicOff, Volume2, Crown, Sparkles, Trash2, MessageSquare,
} from "lucide-react";

const AGENT_META = {
  speaking_coach: { name: "TCF Speaking Coach", voice: true },
  vocabulary_coach: { name: "Vocabulary Coach", voice: false },
  sentence_builder: { name: "Sentence Builder", voice: false },
  point_of_view: { name: "Point of View Coach", voice: false },
};

function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`} className="font-semibold text-primary bg-primary/10 rounded px-1 py-0.5 mx-0.5 box-decoration-clone">{p.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyPrefix}-${i}`}>{p}</span>
    )
  );
}

function MessageContent({ text }) {
  const lines = String(text).split("\n");
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) { blocks.push(<ul key={`ul-${key}`} className="my-1.5 space-y-1">{list}</ul>); list = []; }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flush(idx); return; }
    const bullet = line.match(/^(?:[-•*]|\d+[.)])\s+(.*)/);
    if (bullet) {
      list.push(
        <li key={`li-${idx}`} className="flex gap-2 text-sm"><span className="text-primary mt-0.5 shrink-0">•</span><span>{renderInline(bullet[1], idx)}</span></li>
      );
      return;
    }
    flush(idx);
    const isHeader = (line === line.toUpperCase() && /^[A-ZÀ-Ü0-9 '/()–-]{3,40}:?$/.test(line)) || (/:$/.test(line) && line.length <= 42);
    if (isHeader) {
      blocks.push(<p key={`h-${idx}`} className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mt-3 first:mt-0">{line.replace(/:$/, "")}</p>);
    } else {
      blocks.push(<p key={`p-${idx}`} className="text-sm leading-relaxed my-1">{renderInline(line, idx)}</p>);
    }
  });
  flush("end");
  return <div className="break-words">{blocks}</div>;
}

function pickVoice(prefixes) {
  const voices = window.speechSynthesis.getVoices() || [];
  for (const p of prefixes) {
    const v = voices.find((x) => x.lang && x.lang.toLowerCase().startsWith(p));
    if (v) return v;
  }
  return null;
}

// Read replies aloud with the RIGHT accent: English/Hinglish narration in an English
// voice, and **bold** French phrases in a French voice — no more French-accented Hinglish.
function speakText(text) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const frVoice = pickVoice(["fr"]);
  const enVoice = pickVoice(["en-gb", "en-us", "en-in", "en"]);
  const segments = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  segments.forEach((seg) => {
    const isFrench = seg.startsWith("**") && seg.endsWith("**");
    const clean = seg.replace(/\*\*/g, "").replace(/[#>_`|]/g, " ").replace(/^\s*[-•*]\s*/gm, "").trim();
    if (!clean) return;
    const u = new SpeechSynthesisUtterance(clean);
    if (isFrench) { u.lang = "fr-FR"; if (frVoice) u.voice = frVoice; u.rate = 0.9; }
    else { u.lang = enVoice?.lang || "en-US"; if (enVoice) u.voice = enVoice; u.rate = 0.98; }
    window.speechSynthesis.speak(u);
  });
}

export default function AgentChat() {
  const { agentKey } = useParams();
  const navigate = useNavigate();
  const meta = AGENT_META[agentKey] || { name: "Coach", voice: false };

  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [denied, setDenied] = useState(false);
  const [starters, setStarters] = useState([]);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const openSession = useCallback(async (id) => {
    const { data } = await api.get(`/sessions/${id}`);
    setActiveId(id);
    setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
  }, []);

  const newChat = useCallback(async () => {
    try {
      const { data } = await api.post(`/agents/${agentKey}/sessions`);
      setSessions((prev) => [data.session, ...prev]);
      setActiveId(data.session.id);
      setMessages([{ role: "assistant", content: data.opening.content }]);
      setDenied(false);
    } catch (e) {
      if (e.response?.status === 402) setDenied(true);
      else toast.error("Could not start a session.");
    }
  }, [agentKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meta2 = await api.get("/agents");
        const a = meta2.data.agents.find((x) => x.key === agentKey);
        if (a) setStarters(a.starters || []);
        const sess = (await api.get(`/agents/${agentKey}/sessions`)).data;
        if (cancelled) return;
        if (sess.length) { setSessions(sess); await openSession(sess[0].id); }
        else { await newChat(); }
      } catch { /* handled */ }
    })();
    return () => { cancelled = true; window.speechSynthesis?.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentKey]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming || !activeId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    await streamAgentMessage(activeId, content, {
      onDelta: (d) => setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d };
        return c;
      }),
      onError: (msg, status) => {
        setStreaming(false);
        if (status === 402) { setDenied(true); setMessages((m) => m.slice(0, -1)); }
        else toast.error(msg || "Error");
      },
      onDone: () => setStreaming(false),
    });
  };

  const toggleMic = async () => {
    if (listening) { if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop(); setListening(false); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice input not supported in this browser."); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "speech.webm");
          fd.append("language", "fr");
          const { data } = await api.post("/speech/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.transcript) setInput((prev) => (prev ? prev + " " : "") + data.transcript);
          else toast.info("Nothing heard — try again.");
        } catch (e) { toast.error(e.response?.data?.detail || "Transcription failed."); }
        finally { setTranscribing(false); }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setListening(true);
    } catch { toast.error("Microphone access denied."); }
  };

  const removeSession = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/sessions/${id}`);
    const remaining = sessions.filter((s) => s.id !== id);
    setSessions(remaining);
    if (id === activeId) {
      if (remaining.length) openSession(remaining[0].id);
      else newChat();
    }
  };

  const showStarters = messages.length <= 1;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="agent-chat-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate("/coaches")} data-testid="chat-back-btn" className="h-9 w-9 grid place-items-center rounded-full bg-secondary hover:bg-accent transition-colors shrink-0"><ArrowLeft className="h-4 w-4" /></button>
            <div className="h-9 w-9 rounded-xl emerald-surface grid place-items-center shrink-0"><Sparkles className="h-4 w-4 text-white" /></div>
            <div className="min-w-0">
              <p className="font-semibold leading-tight truncate">{meta.name}</p>
              <p className="text-xs text-muted-foreground">{meta.voice ? "Voice-ready · Hinglish" : "Hinglish coaching"}</p>
            </div>
          </div>
          <button onClick={newChat} data-testid="new-chat-btn" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">
            <Plus className="h-4 w-4" /> New
          </button>
        </div>
      </header>

      <div className="flex-1 flex max-w-5xl w-full mx-auto">
        {/* Sessions sidebar */}
        {sessions.length > 1 && (
          <aside className="hidden lg:block w-56 shrink-0 border-r border-border p-3 space-y-1">
            <p className="text-xs font-bold uppercase text-muted-foreground px-2 py-2">History</p>
            {sessions.map((s) => (
              <button key={s.id} onClick={() => openSession(s.id)} data-testid={`session-${s.id}`}
                className={`w-full group flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${s.id === activeId ? "bg-secondary font-semibold" : "hover:bg-secondary/60"}`}>
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate flex-1">{new Date(s.created_at).toLocaleDateString()}</span>
                <span onClick={(e) => removeSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-destructive"><Trash2 className="h-3.5 w-3.5" /></span>
              </button>
            ))}
          </aside>
        )}

        {/* Chat column */}
        <div className="flex-1 flex flex-col relative">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6" style={{ maxHeight: "calc(100vh - 8rem)" }}>
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${i}`}>
                  <div className={`relative max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"}`}>
                    {m.role === "assistant" && !m.content && streaming ? (
                      <span className="inline-flex gap-1 items-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> réflexion…</span>
                    ) : <MessageContent text={m.content} />}
                    {m.role === "assistant" && meta.voice && m.content && (
                      <button onClick={() => speakText(m.content)} title="Écouter" className="mt-2 inline-flex items-center gap-1 text-xs text-primary font-semibold"><Volume2 className="h-3.5 w-3.5" /> Écouter</button>
                    )}
                  </div>
                </div>
              ))}

              {showStarters && starters.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-starters">
                  {starters.map((s) => (
                    <button key={s} onClick={() => send(s)} className="px-3.5 py-2 rounded-full bg-secondary hover:bg-accent text-sm font-medium transition-colors">{s}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Input */}
          <div className="border-t border-border bg-background/80 backdrop-blur px-4 py-3">
            <div className="max-w-3xl mx-auto flex items-end gap-2">
              {meta.voice && (
                <button onClick={toggleMic} data-testid="mic-btn" title="Voice input" disabled={transcribing}
                  className={`h-11 w-11 shrink-0 grid place-items-center rounded-full transition-colors disabled:opacity-60 ${listening ? "bg-destructive text-destructive-foreground animate-pulse" : "bg-secondary hover:bg-accent"}`}>
                  {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </button>
              )}
              <textarea
                data-testid="chat-input" value={input} rows={1}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={meta.voice ? "Type or tap the mic to speak…" : "Type your message…"}
                className="flex-1 resize-none max-h-32 px-4 py-3 rounded-2xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring text-sm" />
              <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="chat-send-btn"
                className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50">
                {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </div>
            {meta.voice && <p className="max-w-3xl mx-auto text-[11px] text-muted-foreground mt-1.5 px-1">🎙️ Voice mode: tap the mic, speak French, tap again to stop — we transcribe with Whisper. Tap “Écouter” to hear replies.</p>}
          </div>

          {/* Access denied overlay */}
          {denied && (
            <div className="absolute inset-0 z-20 bg-background/85 backdrop-blur-sm grid place-items-center p-6" data-testid="access-denied-overlay">
              <div className="max-w-sm text-center rounded-3xl bg-card border border-border p-8 shadow-xl">
                <div className="h-14 w-14 rounded-2xl emerald-surface grid place-items-center mx-auto"><Crown className="h-7 w-7 text-white" /></div>
                <h3 className="font-display text-2xl font-semibold mt-4">Your free day is over</h3>
                <p className="text-muted-foreground mt-2 text-sm">Upgrade to Premium for unlimited coaching with all four AI coaches.</p>
                <Link to="/premium" data-testid="denied-premium-btn" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">
                  <Crown className="h-4 w-4" /> Go Premium
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
